import { createApp } from "vue";
import AiHeaderButton from "./AiHeaderButton.vue";

frappe.provide("frappe.ai");
frappe.ai.AiHeaderButton = AiHeaderButton;

const CHAT_ASSET_BASE = "/assets/dokos_ai/chat/";

/**
 * Loads the chat widget the first time the assistant is opened.
 *
 * The widget is a separate Vite build (frontend/vite.desk.config.ts) rather than part of
 * this bundle: this file is injected into every desk page by app_include_js, and Frappe's
 * esbuild pipeline cannot code-split, so bundling frappe-ui here would put ~180 kB gzipped
 * on every page load for a feature most page views never touch.
 *
 * The filenames carry a content hash and are looked up through manifest.json, because
 * frappe.require cannot take a `?v=` cache-busting query: AssetManager.extn() reads the
 * text after the `?` and would find no handler for the asset.
 */
frappe.ai.load_chat_widget = async function () {
	if (window.DokosAIChat) return window.DokosAIChat;

	const response = await fetch(CHAT_ASSET_BASE + "manifest.json", { cache: "no-cache" });
	if (!response.ok)
		throw new Error("Dokos AI: chat widget manifest is missing — run `bench build`.");
	const manifest = await response.json();

	await frappe.require([CHAT_ASSET_BASE + manifest.js, CHAT_ASSET_BASE + manifest.css]);
	return window.DokosAIChat;
};

/** What the user is looking at, so the agent does not have to ask. */
function current_form_context() {
	const frm = window.cur_frm;
	if (!frm || !frm.doc || frm.doc.__islocal) return { context: null, currentDoc: null };
	return {
		context: { doctype: frm.doc.doctype, name: frm.doc.name },
		// Stored values, so a proposed write can be rendered as a before/after diff.
		currentDoc: { ...frm.doc },
	};
}

// Thread browser dialog (header button)
frappe.ai.ThreadBrowser = class ThreadBrowser {
	// opts: { threadId: string }
	constructor(opts = {}) {
		Object.assign(this, opts);
		if (frappe.ai.active_thread_browser) {
			frappe.ai.active_thread_browser.dialog.show();
			document.dispatchEvent(new CustomEvent("ai_thread_opened"));
			return;
		}
		this.make();
	}

	make() {
		this.dialog = new frappe.ui.Dialog({
			title: "",
			size: "large",
			on_hide: () => {
				document.dispatchEvent(new CustomEvent("ai_thread_minimized"));
			},
		});

		const $modal = $(this.dialog.$wrapper);
		$modal.addClass("ai-assistant-modal");
		// Hide the default title bar — our Vue toolbar replaces it
		$modal.find(".modal-header").hide();

		const $body = $modal.find(".modal-body");
		$body.css("padding", "0");

		this.dialog.show();

		this.mount_widget($body[0]);

		frappe.ai.active_thread_browser = this;
		document.dispatchEvent(new CustomEvent("ai_thread_opened"));
	}

	async mount_widget(element) {
		element.innerHTML = `<div class="text-muted p-4">${__("Loading the assistant…")}</div>`;
		try {
			const widget = await frappe.ai.load_chat_widget();
			element.innerHTML = "";
			this.host = element;
			widget.mount(element, {
				...current_form_context(),
				threadId: this.threadId || null,
				onClose: () => this.destroy(),
			});
		} catch (e) {
			element.innerHTML = `<div class="text-danger p-4">${__(
				"Could not load the assistant."
			)}</div>`;
			console.error(e);
		}
	}

	minimize() {
		this.dialog.hide();
	}

	destroy(hide_dialog = true) {
		if (this.host && window.DokosAIChat) {
			window.DokosAIChat.unmount(this.host);
			this.host = null;
		}
		if (hide_dialog && this.dialog) {
			this.dialog.hide();
		}
		frappe.ai.active_thread_browser = null;
		document.dispatchEvent(new CustomEvent("ai_thread_closed"));
	}
};

// Inject the AI Assistant button into .body-sidebar-bottom, above the user avatar.
// The guard inside prevents double-mounting, so this is safe to call repeatedly.
frappe.ai.inject_sidebar_button = function () {
	if (frappe.ai.is_enabled === false) return;

	const $bottom = $(".body-sidebar-bottom");
	if (!$bottom.length || $bottom.find(".ai-sidebar-btn-wrapper").length) return;

	if (frappe.ai.is_enabled === undefined) {
		frappe.call({
			method: "dokos_ai.dokos_ai.executor.is_ai_enabled",
			callback: (r) => {
				frappe.ai.is_enabled = !!r.message;
				if (frappe.ai.is_enabled) {
					frappe.ai.inject_sidebar_button();
				}
			},
		});
		return;
	}

	const $wrapper = $('<div class="ai-sidebar-btn-wrapper"></div>');
	const $userBtn = $bottom.find(".dropdown-navbar-user");
	if ($userBtn.length) {
		$wrapper.insertBefore($userBtn);
	} else {
		$wrapper.appendTo($bottom);
	}
	createApp(AiHeaderButton).mount($wrapper[0]);
};

/** Opens the assistant, reusing the existing panel if one is already around. */
frappe.ai.open_assistant = function () {
	new frappe.ai.ThreadBrowser();
};

// app_ready fires once after frappe.ui.Sidebar is constructed (make_dom is synchronous,
// so .body-sidebar-bottom is already in the DOM at this point).
$(document).on("app_ready", () => {
	frappe.ai.inject_sidebar_button();

	// A 14px star in the sidebar footer was the only way in. Two more, for people who
	// navigate by keyboard or by search rather than by hunting for an icon.
	frappe.ui.keys.add_shortcut({
		shortcut: "ctrl+shift+a",
		description: __("Open the Dokos AI assistant"),
		// Deliberately fires while a field has focus: the usual reason to want the
		// assistant is the thing being typed.
		ignore_inputs: false,
		condition: () => frappe.ai.is_enabled !== false,
		action: () => frappe.ai.open_assistant(),
	});

	frappe.search?.utils?.make_function_searchable?.(
		() => frappe.ai.open_assistant(),
		__("Open the Dokos AI assistant")
	);
});

// The sidebar is rebuilt on some navigations (workspace switches in particular), which
// silently took the button with it — it was only ever injected once, on app_ready.
frappe.router.on("change", () => frappe.ai.inject_sidebar_button());
